import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FB_CONFIG_KEYS, fetchRemoteConfig } from '../firebase/remoteConfig';
import { useErrorManager } from 'InternalLibrary/app';

type Props = {
  children: ReactNode;
};

type FirebaseTestAB = {
  [FB_CONFIG_KEYS.FIREBASE_TESTAB_TINK_A]: number;
  [FB_CONFIG_KEYS.FIREBASE_TESTAB_TINK_IFRAME]: number;
  [FB_CONFIG_KEYS.FIREBASE_TESTAB_TINK_ASYNC]: number;
};

type FirebaseData = {
  [FB_CONFIG_KEYS.FIREBASE_DATADOG_LOGS_INFO]: boolean;
  [FB_CONFIG_KEYS.FIREBASE_TINK_IFRAME_WAITING_SCREEN]: boolean;
};

type FirebaseContextType = {
  firebaseData: FirebaseData;
  firebaseTestAB: FirebaseTestAB;
  fetchFirebaseData: () => void;
};

const FirebaseContext = createContext<FirebaseContextType>(
  {} as FirebaseContextType,
);

const useFirebase = (): FirebaseContextType => useContext(FirebaseContext);

const FirebaseProvider = ({ children }: Props): JSX.Element => {
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  const [firebaseData, setFirebaseData] = useState<FirebaseData>({
    [FB_CONFIG_KEYS.FIREBASE_DATADOG_LOGS_INFO]: false,
    [FB_CONFIG_KEYS.FIREBASE_TINK_IFRAME_WAITING_SCREEN]: false,
  });
  const [firebaseTestAB, setFirebaseTestAB] = useState<FirebaseTestAB>({
    [FB_CONFIG_KEYS.FIREBASE_TESTAB_TINK_A]: 70,
    [FB_CONFIG_KEYS.FIREBASE_TESTAB_TINK_IFRAME]: 70,
    [FB_CONFIG_KEYS.FIREBASE_TESTAB_TINK_ASYNC]: 0,
  });
  const { setError } = useErrorManager();

  const fetchFirebaseData = useCallback(async (): Promise<any> => {
    try {
      const firebaseValues = await fetchRemoteConfig();

      const testABTinkA = Number(
        firebaseValues[FB_CONFIG_KEYS.FIREBASE_TESTAB_TINK_A]?._value as string,
      );
      const testABTinkIframe = Number(
        firebaseValues[FB_CONFIG_KEYS.FIREBASE_TESTAB_TINK_IFRAME]
          ?._value as string,
      );
      const testABTinkAsync = Number(
        firebaseValues[FB_CONFIG_KEYS.FIREBASE_TESTAB_TINK_ASYNC]
          ?._value as string,
      );

      setFirebaseTestAB({
        [FB_CONFIG_KEYS.FIREBASE_TESTAB_TINK_A]: testABTinkA,
        [FB_CONFIG_KEYS.FIREBASE_TESTAB_TINK_IFRAME]: testABTinkIframe,
        [FB_CONFIG_KEYS.FIREBASE_TESTAB_TINK_ASYNC]: testABTinkAsync,
      });

      const enableDatadogInfo = Boolean(
        firebaseValues[FB_CONFIG_KEYS.FIREBASE_DATADOG_LOGS_INFO]
          ?._value as string,
      );

      const enableWaitingScreenForIframe = Boolean(
        firebaseValues[FB_CONFIG_KEYS.FIREBASE_TINK_IFRAME_WAITING_SCREEN]
          ?._value as string,
      );

      setFirebaseData({
        [FB_CONFIG_KEYS.FIREBASE_DATADOG_LOGS_INFO]: enableDatadogInfo,
        [FB_CONFIG_KEYS.FIREBASE_TINK_IFRAME_WAITING_SCREEN]:
          enableWaitingScreenForIframe,
      });

      setIsDataLoaded(true);
    } catch (e) {
      setError(e);
    }
  }, []);

  const FirebaseContextValue = useMemo(
    () => ({
      firebaseData,
      firebaseTestAB,
      fetchFirebaseData,
    }),
    [fetchFirebaseData, firebaseData, firebaseTestAB],
  );

  useEffect(() => {
    if (!isDataLoaded) fetchFirebaseData();
  }, [isDataLoaded]);

  return (
    <FirebaseContext.Provider value={FirebaseContextValue}>
      {children}
    </FirebaseContext.Provider>
  );
};

export { useFirebase as default, FirebaseProvider };
